import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export const Autocomplete = ({ value, onChange, onSelect, options = [], placeholder, testId, icon = false, className = "", strict = false }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const [pos, setPos] = useState(null);
  const [active, setActive] = useState(-1);
  const ref = useRef(null);
  const listRef = useRef(null);
  const filtered = useMemo(() => {
    const k = typed ? (value || "").toLowerCase().trim() : "";
    return options.filter((o) => !k || o.label.toLowerCase().includes(k) || String(o.sub || "").toLowerCase().includes(k)).slice(0, 30);
  }, [value, options, typed]);

  const place = () => {
    const el = ref.current;
    if (!el) return;
    const host = el.closest('[role="dialog"]') || document.body;
    const r = el.getBoundingClientRect();
    const o = host === document.body ? { left: 0, top: 0 } : host.getBoundingClientRect();
    const st = host === document.body ? 0 : host.scrollTop;
    setPos({ host, left: r.left - o.left, top: r.bottom + 4 - o.top + st, width: Math.max(r.width, 240), fixed: host === document.body });
  };
  useLayoutEffect(() => { if (open) place(); }, [open, value]);
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape" && ref.current?.contains(document.activeElement)) {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onEsc, true);
    return () => window.removeEventListener("keydown", onEsc, true);
  }, [open]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(0); return; }
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && open && active >= 0 && filtered[active]) {
      e.preventDefault();
      pick(filtered[active]);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  const pick = (o) => {
    if (onSelect) onSelect(o);
    else onChange(o.label);
    setOpen(false);
    setActive(-1);
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      {icon && <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />}
      <Input
        value={value || ""}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
        title={value || ""}
        className={`bg-white ${icon ? "pl-9" : ""}`}
        onChange={(e) => { onChange(e.target.value); setTyped(true); setOpen(true); setActive(-1); }}
        onFocus={() => { setTyped(false); setOpen(true); setActive(-1); }}
        onKeyDown={onKey}
        onBlur={() => setTimeout(() => {
          setOpen(false);
          if (strict && value && !options.some((o) => o.label === value)) onChange("");
        }, 160)}
      />
      {open && pos && filtered.length > 0 && createPortal(
        <div ref={listRef} data-testid={`${testId}-dropdown`} style={{ position: pos.fixed ? "fixed" : "absolute", left: pos.left, top: pos.top, width: pos.width }}
          className="z-[1000] max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white shadow-xl fade-up">
          {filtered.map((o, i) => (
            <button type="button" key={`${o.label}-${i}`} data-idx={i} data-testid={`${testId}-option-${i}`} data-active={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors ${i === active ? "bg-blue-100 text-blue-900" : "hover:bg-blue-50"}`}>
              <span className="font-medium text-slate-800">{o.label}</span>
              {o.sub ? <span className="text-xs text-slate-500">{o.sub}</span> : null}
            </button>
          ))}
        </div>,
        pos.host,
      )}
    </div>
  );
};
